import { useAllWidgets } from '../bff-api/bloomreach-recommendation/useAllWidgets';
import ClientRecommendations from '../components/recommendations/ClientRecommendations';
import { RecommendationLocation } from '../components/recommendations/RecommendationLocation';
import type { BloomreachWidget } from '../bff-api/bloomreach-recommendation/model/BloomreachWidget';

export interface RecommendationPlaces {
    location: RecommendationLocation;
    categoryId?: string;
    productId?: string;
    brandCategoryId?: string;
    collectionId?: string;
    userId?: string;
}

const MAXIMUM_RECOMMENDATIONS_CART = 1;
const MAXIMUM_RECOMMENDATIONS_IN_PDP = 3;

export const useRecommendations = ({
    location,
    categoryId,
    productId,
    brandCategoryId,
    collectionId,
    userId,
}: RecommendationPlaces): JSX.Element[] | undefined | null => {
    const { data } = useAllWidgets();

    if (!data) {
        return null;
    }

    if (location === RecommendationLocation.SHOPPING_BAG && !userId) {
        return null;
    }

    let widgets: BloomreachWidget[] = [];

    if (location === RecommendationLocation.DRAWER) {
        widgets = data
            .filter((widget) => widget.orderOfAppearanceDisplayValue === 'Cart Modal')
            .slice(0, MAXIMUM_RECOMMENDATIONS_CART);
    }

    if (
        location === RecommendationLocation.HOME_PAGE ||
        location === RecommendationLocation.PDP_ERROR
    ) {
        widgets = data.filter((widget) => widget.orderOfAppearanceDisplayValue === '');
    }

    if (location === RecommendationLocation.PDP) {
        widgets = data
            .filter(({ orderOfAppearanceDisplayValue, name }) => {
                const isExcluded =
                    ['Cart Modal', 'Shopping Bag'].includes(orderOfAppearanceDisplayValue) ||
                    name === 'search_recently_viewed_products';
                const hasValidDisplayValue = orderOfAppearanceDisplayValue.length > 0;
                return !isExcluded && hasValidDisplayValue;
            })
            .sort((a, b) => a.orderOfAppearanceValue - b.orderOfAppearanceValue)
            .slice(0, MAXIMUM_RECOMMENDATIONS_IN_PDP);
    }

    if (location === RecommendationLocation.SHOPPING_BAG) {
        widgets = data.filter((widget) => widget.orderOfAppearanceDisplayValue === 'Shopping Bag');
    }

    if (location === RecommendationLocation.SEARCH_RESULTS) {
        widgets = data.filter((widget) => widget.name === 'search_recently_viewed_products');
    }

    if (location === RecommendationLocation.SEARCH_NO_RESULTS) {
        widgets = data.filter((widget) => widget.name === 'home_trending_products');
    }

    return widgets.map((widget) => (
        <ClientRecommendations
            key={widget.id}
            widget={widget}
            categoryId={categoryId}
            productId={productId}
            brandCategoryId={brandCategoryId}
            collectionId={collectionId}
            location={location}
            productTypeFilter="hardgood"
            userId={userId}
        />
    ));
};
